import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, Save, X, Layers, ExternalLink, Users, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const CATEGORIES = {
    "firearm-safety": "Firearm safety",
    "hunter-safety": "Hunter safety",
    "outdoor-skills": "Outdoor skills",
    "first-aid": "First aid",
    "financial-literacy": "Financial literacy",
    "civics": "Civics",
    "digital-skills": "Digital skills",
    "life-skills": "Life skills",
    "other": "Other",
};

const blankCourse = {
    title: "", slug: "", summary: "", category: "other",
    difficulty: "beginner", instructor: "", cover_image: "", active: true,
};

const blankLesson = {
    title: "", body: "", video_url: "", resources: [], estimated_minutes: 10,
};

const CoursesPanel = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [editingCourseId, setEditingCourseId] = useState(null);
    const [courseForm, setCourseForm] = useState(blankCourse);

    const [openCourse, setOpenCourse] = useState(null);  // course currently being edited (lessons inside modal)
    const [lessons, setLessons] = useState([]);
    const [editingLessonId, setEditingLessonId] = useState(null);
    const [lessonForm, setLessonForm] = useState(blankLesson);
    const [addingLesson, setAddingLesson] = useState(false);

    const loadCourses = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/courses");
            setCourses(data || []);
        } catch { toast.error("Could not load courses"); }
        finally { setLoading(false); }
    };
    useEffect(() => { loadCourses(); }, []);

    const loadLessons = async (courseId) => {
        try {
            const { data } = await api.get(`/admin/courses/${courseId}/lessons`);
            setLessons(data || []);
        } catch { toast.error("Could not load lessons"); }
    };

    const resetCourse = () => { setAdding(false); setEditingCourseId(null); setCourseForm(blankCourse); };
    const resetLesson = () => { setAddingLesson(false); setEditingLessonId(null); setLessonForm(blankLesson); };

    const saveCourse = async () => {
        if (!courseForm.title.trim()) { toast.error("Title required"); return; }
        try {
            if (editingCourseId) await api.patch(`/admin/courses/${editingCourseId}`, courseForm);
            else await api.post("/admin/courses", courseForm);
            toast.success("Course saved");
            resetCourse();
            await loadCourses();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    const startEditCourse = (c) => {
        setEditingCourseId(c.id); setAdding(false);
        setCourseForm({
            title: c.title, slug: c.slug, summary: c.summary || "",
            category: c.category, difficulty: c.difficulty,
            instructor: c.instructor || "", cover_image: c.cover_image || "",
            active: c.active,
        });
    };

    const removeCourse = async (c) => {
        if (!window.confirm(`Delete "${c.title}" and all its lessons + enrollments?`)) return;
        try { await api.delete(`/admin/courses/${c.id}`); toast.success("Deleted"); await loadCourses(); }
        catch { toast.error("Delete failed"); }
    };

    const openLessonsManager = async (c) => {
        setOpenCourse(c);
        await loadLessons(c.id);
    };

    const saveLesson = async () => {
        if (!lessonForm.title.trim()) { toast.error("Lesson title required"); return; }
        try {
            if (editingLessonId) await api.patch(`/admin/lessons/${editingLessonId}`, lessonForm);
            else await api.post(`/admin/courses/${openCourse.id}/lessons`, lessonForm);
            toast.success("Lesson saved");
            resetLesson();
            await loadLessons(openCourse.id);
            await loadCourses();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    const startEditLesson = (l) => {
        setEditingLessonId(l.id); setAddingLesson(false);
        setLessonForm({
            title: l.title, body: l.body || "", video_url: l.video_url || "",
            resources: l.resources || [], estimated_minutes: l.estimated_minutes,
        });
    };

    const removeLesson = async (l) => {
        if (!window.confirm(`Delete lesson "${l.title}"?`)) return;
        try { await api.delete(`/admin/lessons/${l.id}`); toast.success("Lesson deleted"); await loadLessons(openCourse.id); await loadCourses(); }
        catch { toast.error("Delete failed"); }
    };

    const addResource = () => setLessonForm({ ...lessonForm, resources: [...(lessonForm.resources || []), { label: "", url: "" }] });
    const updateResource = (i, key, val) => {
        const next = [...(lessonForm.resources || [])];
        next[i] = { ...next[i], [key]: val };
        setLessonForm({ ...lessonForm, resources: next });
    };
    const removeResource = (i) => setLessonForm({ ...lessonForm, resources: (lessonForm.resources || []).filter((_, idx) => idx !== i) });

    return (
        <div className="space-y-6" data-testid="courses-panel">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252e] tracking-tight">Continuing education</h2>
                    <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">Free mini-courses for the community — hunter safety, firearm safety, first aid, civics. Each course holds an ordered list of lessons. Learners can enroll anonymously and get a completion certificate once they finish all lessons.</p>
                </div>
                <button onClick={() => { setAdding(true); setEditingCourseId(null); setCourseForm(blankCourse); }} data-testid="course-add-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium">
                    <Plus className="w-4 h-4" strokeWidth={2} /> New course
                </button>
            </div>

            {(adding || editingCourseId) && (
                <div className="bg-white border border-[#E53935]/30 rounded-2xl p-5 space-y-3" data-testid="course-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Title *</label>
                            <input data-testid="course-title-input" value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" placeholder="Hunter Safety 101" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">URL slug (auto from title if blank)</label>
                            <input data-testid="course-slug-input" value={courseForm.slug} onChange={(e) => setCourseForm({ ...courseForm, slug: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" placeholder="hunter-safety-101" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Summary</label>
                        <textarea rows={2} data-testid="course-summary-input" value={courseForm.summary} onChange={(e) => setCourseForm({ ...courseForm, summary: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <select data-testid="course-category-select" value={courseForm.category} onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })} className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40">
                            {Object.entries(CATEGORIES).map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
                        </select>
                        <select data-testid="course-difficulty-select" value={courseForm.difficulty} onChange={(e) => setCourseForm({ ...courseForm, difficulty: e.target.value })} className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40">
                            <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
                        </select>
                        <input data-testid="course-instructor-input" value={courseForm.instructor} onChange={(e) => setCourseForm({ ...courseForm, instructor: e.target.value })} placeholder="Instructor name" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                    </div>
                    <input data-testid="course-cover-input" value={courseForm.cover_image} onChange={(e) => setCourseForm({ ...courseForm, cover_image: e.target.value })} placeholder="Cover image URL (optional)" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                    <label className="flex items-center gap-2 text-sm text-[#1C252E]">
                        <input type="checkbox" data-testid="course-active-checkbox" checked={courseForm.active} onChange={(e) => setCourseForm({ ...courseForm, active: e.target.checked })} /> Visible on public /learn page
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={resetCourse} className="text-sm text-[#4A5568] hover:text-[#1C252E] inline-flex items-center gap-1"><X className="w-4 h-4" strokeWidth={2} /> Cancel</button>
                        <button onClick={saveCourse} data-testid="course-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium"><Save className="w-4 h-4" strokeWidth={2} /> {editingCourseId ? "Save" : "Create"}</button>
                    </div>
                </div>
            )}

            {loading ? <p className="text-sm text-[#4A5568]">Loading…</p> : courses.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="courses-empty">No courses yet.</p>
            ) : (
                <ul className="space-y-3" data-testid="courses-list">
                    {courses.map((c) => (
                        <li key={c.id} data-testid={`course-row-${c.id}`} className="bg-white border border-black/10 rounded-2xl p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${c.active ? "bg-green-100 text-green-800" : "bg-[#F4F1EA] text-[#4A5568]"}`}>{c.active ? "Live" : "Hidden"}</span>
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">{CATEGORIES[c.category] || c.category} · {c.difficulty}</span>
                                    </div>
                                    <p className="font-medium text-[#1C252E]">{c.title}</p>
                                    <p className="text-xs text-[#4A5568] mt-0.5 flex items-center gap-2">
                                        <Layers className="w-3 h-3" strokeWidth={1.5} /> {c.lesson_count} lessons · {c.estimated_minutes} min
                                        {c.instructor && <span>· {c.instructor}</span>}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 flex-wrap">
                                    <button onClick={() => openLessonsManager(c)} data-testid={`course-lessons-${c.id}`} className="inline-flex items-center gap-1 text-xs bg-[#1C252E] hover:bg-black text-white rounded-full px-3 py-1.5 font-medium">
                                        <Layers className="w-3 h-3" strokeWidth={2} /> Lessons ({c.lesson_count})
                                    </button>
                                    <a href={`/learn/${c.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`course-view-${c.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="View public page"><ExternalLink className="w-4 h-4" strokeWidth={1.5} /></a>
                                    <button onClick={() => startEditCourse(c)} data-testid={`course-edit-${c.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]"><Edit2 className="w-4 h-4" strokeWidth={1.5} /></button>
                                    <button onClick={() => removeCourse(c)} data-testid={`course-delete-${c.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {openCourse && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="lessons-modal">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between p-5 border-b border-black/5">
                            <div className="min-w-0">
                                <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">Lessons</p>
                                <h3 className="font-heading text-lg text-[#1C252E] truncate">{openCourse.title}</h3>
                            </div>
                            <div className="flex items-center gap-1">
                                <button onClick={() => { setAddingLesson(true); setEditingLessonId(null); setLessonForm(blankLesson); }} data-testid="lesson-add-btn" className="inline-flex items-center gap-1 text-xs bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-3 py-1.5 font-medium">
                                    <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Lesson
                                </button>
                                <button onClick={() => { setOpenCourse(null); resetLesson(); }} className="p-2 rounded-full hover:bg-[#F4F1EA]" aria-label="Close"><X className="w-4 h-4 text-[#4A5568]" strokeWidth={1.5} /></button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5 space-y-3">
                            {(addingLesson || editingLessonId) && (
                                <div className="bg-[#FDFBF7] border border-[#E53935]/30 rounded-2xl p-4 space-y-3" data-testid="lesson-form">
                                    <input data-testid="lesson-title-input" value={lessonForm.title} onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })} placeholder="Lesson title *" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                    <textarea rows={5} data-testid="lesson-body-input" value={lessonForm.body} onChange={(e) => setLessonForm({ ...lessonForm, body: e.target.value })} placeholder="Lesson content (markdown or plain text)" className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <input data-testid="lesson-video-input" value={lessonForm.video_url} onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })} placeholder="Video embed URL (YouTube, Vimeo)" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                        <input type="number" min="1" data-testid="lesson-minutes-input" value={lessonForm.estimated_minutes} onChange={(e) => setLessonForm({ ...lessonForm, estimated_minutes: parseInt(e.target.value, 10) || 10 })} placeholder="Est. minutes" className="rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40" />
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Resources / Links</p>
                                        {(lessonForm.resources || []).map((r, i) => (
                                            <div key={i} className="flex gap-2">
                                                <input value={r.label || ""} onChange={(e) => updateResource(i, "label", e.target.value)} placeholder="Label" className="flex-1 rounded-xl border border-black/10 px-3 py-2 bg-white text-sm" />
                                                <input value={r.url || ""} onChange={(e) => updateResource(i, "url", e.target.value)} placeholder="URL" className="flex-1 rounded-xl border border-black/10 px-3 py-2 bg-white text-sm" />
                                                <button onClick={() => removeResource(i)} className="p-2 text-[#E53935]" aria-label="Remove"><X className="w-4 h-4" strokeWidth={2} /></button>
                                            </div>
                                        ))}
                                        <button type="button" onClick={addResource} data-testid="lesson-add-resource" className="text-xs text-[#E53935] hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add resource</button>
                                    </div>
                                    <div className="flex items-center justify-end gap-2 pt-2">
                                        <button onClick={resetLesson} className="text-sm text-[#4A5568] hover:text-[#1C252E] inline-flex items-center gap-1"><X className="w-4 h-4" strokeWidth={2} /> Cancel</button>
                                        <button onClick={saveLesson} data-testid="lesson-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-4 py-1.5 text-sm font-medium"><Save className="w-3.5 h-3.5" strokeWidth={2} /> {editingLessonId ? "Save" : "Add lesson"}</button>
                                    </div>
                                </div>
                            )}

                            {lessons.length === 0 ? (
                                <p className="text-sm text-[#4A5568] italic">No lessons yet. Click <strong>+ Lesson</strong> to add the first one.</p>
                            ) : (
                                <ol className="space-y-2" data-testid="lessons-list">
                                    {lessons.map((l, idx) => (
                                        <li key={l.id} data-testid={`lesson-row-${l.id}`} className="bg-white border border-black/10 rounded-xl p-3 flex items-center gap-3">
                                            <GripVertical className="w-4 h-4 text-[#4A5568]/40" strokeWidth={1.5} />
                                            <span className="text-xs font-bold text-[#4A5568] w-6">{idx + 1}.</span>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-[#1C252E] truncate">{l.title}</p>
                                                <p className="text-[11px] text-[#4A5568]">{l.estimated_minutes} min{l.video_url && " · video"}{l.resources?.length > 0 && ` · ${l.resources.length} resource${l.resources.length === 1 ? "" : "s"}`}</p>
                                            </div>
                                            <button onClick={() => startEditLesson(l)} data-testid={`lesson-edit-${l.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]"><Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                                            <button onClick={() => removeLesson(l)} data-testid={`lesson-delete-${l.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CoursesPanel;
